import { CARE_CODE_CATALOG, careReasonText } from './constants';
import { CareRow, ConditionSummary } from './types';

type ConditionRecord = {
    key: string;
    source: string;
    item: ConditionSummary['items'][number];
};

export const conditionText = ({ source, item }: ConditionRecord): string =>
    `${item.name || item.icd_code || `Condition ${item.record_id}`}${item.name && item.icd_code ? ` (${item.icd_code})` : ''}` +
    `${item.status ? ` · ${item.status}` : ''} · Source: ${source === 'drchrono' ? 'DrChrono' : careReasonText(source) ?? 'Unknown'}`;

// A code filter must not hide the patient's conditions from other loaded rows.
export const patientConditions = (rows: CareRow[]) => {
    const records = new Map<string, ConditionRecord>();
    let unavailable = false;
    rows.forEach((row) => {
        const summary = row.conditions;
        unavailable ||= !summary || summary.state === 'unavailable';
        summary?.items?.forEach((item) => {
            const key = JSON.stringify([summary.source, item.record_id, item.name, item.icd_code, item.icd_version, item.status]);
            records.set(key, { key, source: summary.source, item });
        });
    });
    const message = unavailable
        ? records.size
            ? 'Some condition records are unavailable; this list may be incomplete.'
            : 'Condition records are unavailable for this patient.'
        : !records.size ? 'No conditions recorded for this patient.' : null;
    return { records: Array.from(records.values()), message };
};

export const CPT_CONDITION_DISPLAY_VERSION = 'program-reported-diagnoses-v1';

const normalizedCode = (code: unknown): string => typeof code === 'string' ? code.trim().toUpperCase() : '';

// A reported program diagnosis is a display association, never a clinical billing rule.
export const relatedConditions = (row: CareRow, patientRows: CareRow[]) => {
    const result = (items: string[], message: string, state: string) => ({
        items, message, state, version: CPT_CONDITION_DISPLAY_VERSION
    });
    if (CARE_CODE_CATALOG.get(row.code)?.program !== row.program)
        return result([], 'Condition association not defined for this code.', 'unsupported');
    if (!row.is_current)
        return result([], 'Calculate billable codes to update condition links.', 'stale');
    const diagnosis = row.billing_diagnosis ?? row.current_evidence.facts?.billing_diagnosis;
    const codes = new Set<string>();
    if (diagnosis) {
        if (diagnosis.state === 'missing') return result([], 'Condition not linked.', 'missing');
        if (diagnosis.state !== 'present' && diagnosis.state !== 'conflict')
            return result([], 'Condition link unavailable.', 'unavailable');
        const reported = diagnosis.reported_codes;
        if (Array.isArray(reported)) reported.forEach((code) => {
            const value = normalizedCode(code);
            if (value) codes.add(value);
        });
        if (diagnosis.state === 'present') {
            const value = normalizedCode(diagnosis.code);
            if (value) codes.add(value);
        }
    } else {
        const value = normalizedCode(row.condition_code);
        if (value) codes.add(value);
    }
    if (!codes.size) return result([], 'Condition not linked.', 'missing');
    const conditions = patientConditions(patientRows.filter((candidate) => candidate.hero_user_id === row.hero_user_id));
    const items = Array.from(new Set(Array.from(codes).sort().flatMap((code) => {
        const matches = conditions.records.filter(({ item }) =>
            item.icd_version === 10 && normalizedCode(item.icd_code) === code
        );
        return matches.length ? matches.map(conditionText) : [`Reported code: ${code}`];
    })));
    const conflict = diagnosis?.state === 'conflict' || codes.size > 1;
    const note = conflict
        ? `Multiple diagnoses reported for ${row.program}; no single diagnosis selected.`
        : `Reported for ${row.program}; not clinically verified.`;
    return result(items, conditions.message
        ? `${note} ${conditions.message}` : note, conflict ? 'conflict' : 'reported');
};
